import { useTranslation } from 'react-i18next';
import { Input } from 'components/ui/input';
import { TAX_BASES, type TaxBasis } from 'utils/money';
import { SEGMENTED, segmentClass } from 'components/ui/segmented';

interface TariffFieldProps {
  /** As typed: parsed by `parseMoneyInput` upstream, cents only. */
  text: string;
  taxBasis: TaxBasis;
  onTextChange: (text: string) => void;
  onTaxBasisChange: (taxBasis: TaxBasis) => void;
  refused: boolean;
  /** Somebody accepted: only he hears of a change (F16). */
  accepted?: boolean;
  /** The travel fee inputs (F19) reuse the field with their own id and copy; `hint: null` hides it. */
  id?: string;
  label?: string;
  hint?: string | null;
  error?: string;
}

/** F2/F12 — « Tarif de la mission · facultatif », « € » suffix, HT by default. */
const TariffField = ({
  text,
  taxBasis,
  onTextChange,
  onTaxBasisChange,
  refused,
  accepted = false,
  id = 'assign-tariff',
  label,
  hint,
  error,
}: TariffFieldProps) => {
  const { t } = useTranslation();
  const title = label ?? t('Mission tariff · optional');
  const help = hint === undefined ? t('The tariff agreed on the phone. Visible to the technician.') : hint;
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-[13px] font-medium">
        {title}
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-[140px]">
          <Input
            id={id}
            inputMode="decimal"
            autoComplete="off"
            className="pr-7"
            value={text}
            aria-invalid={refused}
            aria-describedby={help ? `${id}-hint` : undefined}
            onChange={(event) => onTextChange(event.target.value)}
          />
          <span className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center text-[13px] text-muted-foreground">
            €
          </span>
        </div>
        <fieldset className={SEGMENTED}>
          <legend className="sr-only">{title}</legend>
          {TAX_BASES.map((basis) => (
            <label key={basis} className={segmentClass(taxBasis === basis)}>
              <input
                type="radio"
                name={`${id}-basis`}
                className="sr-only"
                checked={taxBasis === basis}
                onChange={() => onTaxBasisChange(basis)}
              />
              {basis}
            </label>
          ))}
        </fieldset>
      </div>
      {help && (
        <p id={`${id}-hint`} className="text-[12px] text-muted-foreground">
          {help}
        </p>
      )}
      {accepted && (
        <p className="text-[12px] text-muted-foreground">{t('Only the retained technician will be notified by e-mail.')}</p>
      )}
      {refused && (
        <p className="text-[12.5px] text-rose-600 dark:text-rose-400" role="alert">
          {error ?? t('Enter an amount, e.g. 180 or 180.50.')}
        </p>
      )}
    </div>
  );
};

export default TariffField;
